import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { PAGE_PX } from '../constants/layout';

/** Horizontal sizing mode for a page. */
export type PageWidth = 'full' | 'form';

interface PageContentProps {
  children: ReactNode;
  /**
   * Horizontal sizing:
   * - `'full'` (default): list/dashboard pages fill the column.
   * - `'form'`: form pages (submit, edit) render in a centered column with a responsive
   *   `max-width`, so the form stays readable and centered on wide screens.
   *
   * Both modes use the same outer page padding (`PAGE_PX`) so the agnostic app header
   * lines up with the page gutters regardless of mode.
   */
  width?: PageWidth;
}

/** Responsive max-width (px) for the centered form column. */
const FORM_MAX_WIDTH = { xs: '100%', md: 760, lg: 920, xl: 1080 };

export default function PageContent({ children, width = 'full' }: PageContentProps) {
  const inner =
    width === 'form' ? (
      <Box sx={{ maxWidth: FORM_MAX_WIDTH, mx: 'auto', width: '100%' }}>{children}</Box>
    ) : (
      children
    );

  return (
    <Box
      sx={{
        pt: { xs: 3, sm: 4, md: 5 },
        pb: { xs: 3, sm: 4, md: 5 },
        px: PAGE_PX,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {inner}
    </Box>
  );
}
